import { Empty, Flex, List } from "antd";
import { Conversation, UpdateConversation } from "../../../interfaces/conversations.type";

import ConversationsCard from "./Conversations.card";
import useGetConversations, { updateConversation } from "../../../hooks/apis/conversations.api";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "../../generic/Loader";
import { useEffect, useMemo, useRef } from "react";
import { SelectedConversaton } from "../../../store/slices/conversation.slice";
import {
    createContactsByPhone,
    getConversationParticipantSummary,
    participantLabel,
} from "../../../utils/conversationDisplay";

/**
 * ConversationsList component - displays a list of dids conversation
 *
 * @param {RootState} state - Redux state
 * @returns {JSX.Element} - Rendered ConversationList component
 */
const ConversationList = ({
    setIsNewConversation,
    convsersationsFilter,
}: {
    setIsNewConversation: Function;
    convsersationsFilter: {
        unread: boolean;
        assigned: boolean;
        unresolved: boolean;
        resolved: boolean;
        assigned_user: string;
        unassigned: boolean;
    };
}): JSX.Element => {
    const { data, selectedConversation } = useSelector((state: RootState) => state.conversation);
    const previousSelectedConversation = useRef<SelectedConversaton | null>(null);

    const { data: conts } = useSelector((state: RootState) => state.contacts);
    const { data: user } = useSelector((state: RootState) => state.sessionData);
    const { selectedDid } = useSelector((state: RootState) => state.smsDids);
    const selectedSmsDid = useSelector((state: RootState) => state.smsDids).data.find(did => did.did === selectedDid);
    const didUsers = useSelector((state: RootState) => state.didUsers)
    const contactsByPhone = useMemo(() => createContactsByPhone(conts), [conts]);

    const filteredData = useMemo(() => data
        .filter((conversation: Conversation) => conversation.did == selectedDid)
        .filter(conversation => (
            (!convsersationsFilter.unread || (conversation.unread_messages_count > 0 || conversation.unread === true)) &&
            (!convsersationsFilter.assigned || conversation.assigned_user !== null) &&
            (!convsersationsFilter.unresolved || !conversation.resolved) &&
            (!convsersationsFilter.resolved || conversation.resolved) &&
            (!convsersationsFilter.unassigned || conversation.assigned_user == null) &&
            (!convsersationsFilter.assigned_user || conversation.assigned_user == user?.id)
        ))
        .sort((a, b) => new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()), [
        data,
        selectedDid,
        convsersationsFilter,
        user?.id,
    ]);
    const conversationDisplayDetails = useMemo(() => filteredData.map((conversation) => {
        const participantSummary = getConversationParticipantSummary(
            conversation.participants,
            contactsByPhone
        );

        return {
            conversation,
            participantSummary,
            participantLabels: conversation.participants.map((participant) =>
                participantLabel(participant, contactsByPhone)
            ),
        };
    }), [contactsByPhone, filteredData]);
    const { fetchNextPage, isLoading, refetch } = useGetConversations({
        did: selectedDid,
        offset: 0,
        limit: 20,
        unread: convsersationsFilter.unread,
        assigned: convsersationsFilter.assigned,
        unresolved: convsersationsFilter.unresolved,
        unassigned: convsersationsFilter.unassigned,
        resolved: convsersationsFilter.resolved,
        assigned_user: convsersationsFilter.assigned_user ? user?.id : null,
    });

    useEffect(() => {
        if(!selectedDid) return;
        refetch();
    }, [selectedDid, convsersationsFilter]);


    useEffect(() => {
        if (data && data.length > 0 && previousSelectedConversation.current !== null) {
            const conv = data.find(c => c.id === previousSelectedConversation.current?.conversation_id);
            if (conv) {
                previousSelectedConversation.current = {
                    ...previousSelectedConversation.current,
                    ...conv,
                    conversation_id: conv.id,
                }
            }
        }
    }, [data])


    useEffect(() => {
        if (selectedConversation && previousSelectedConversation.current && selectedSmsDid?.assigned && (previousSelectedConversation.current.unread_messages_count || 0 > 0 || previousSelectedConversation.current.unread)) {
            const payload: UpdateConversation = {
                did: previousSelectedConversation.current.did,
                conversation_id: previousSelectedConversation.current.conversation_id,
                last_read_timestamp: new Date().toISOString(),
                total_read: previousSelectedConversation.current.unread_messages_count || 0,
                ...(previousSelectedConversation.current.unread ? { unread: false } : {})
            }

            updateConversation(payload)
        }


        if (!selectedConversation) {
            previousSelectedConversation.current = null;
            return;
        }

        if (
            (selectedSmsDid?.assigned && (selectedConversation.unread_messages_count || 0 > 0 || selectedConversation.unread))
        ) {
            const payload: UpdateConversation = {
                did: selectedConversation.did,
                conversation_id: selectedConversation.conversation_id,
                last_read_timestamp: new Date().toISOString(),
                total_read: selectedConversation.unread_messages_count || 0,
                ...(selectedConversation.unread ? { unread: false } : {})
            };
            updateConversation(payload);
        }

        previousSelectedConversation.current = {
            ...selectedConversation,
            unread_messages_count: 0,
            unread: false,
        };
    }, [selectedConversation?.conversation_id]);



    const spinStyle: React.CSSProperties = {
        width: "100%",
        height: "calc(100% - 80px)"
    };

    if (isLoading) {
        return (
            <Flex style={spinStyle} justify="center" align="center">
                <Loader />
            </Flex>
        );
    }

    if (!isLoading && filteredData.length == 0) {
        return (
            <Flex style={spinStyle} justify="center" align="center">
                {<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
            </Flex>
        );
    }

    return (
        <List
            style={{ height: "calc(100% - 80px)", overflow: "auto" }}
            itemLayout="horizontal"
            id="scrollableDiv"
            dataSource={filteredData}
        >
            <InfiniteScroll
                dataLength={filteredData.length}
                next={() => {
                    fetchNextPage();
                }}
                key={"scrollableDiv"}
                hasMore={true}
                loader={""}
                scrollableTarget="scrollableDiv"
            >
                {conversationDisplayDetails
                    .map(({ conversation, participantSummary, participantLabels }, index: number) => {
                        return (
                            <ConversationsCard
                                convName={participantSummary.names}
                                additionalParticipantCount={participantSummary.additionalParticipantCount}
                                participantLabels={participantLabels}
                                isSelected={selectedConversation ? selectedConversation.conversation_id === conversation.id : false}
                                assigned={conversation.assigned_user}
                                setIsNewConversation={setIsNewConversation}
                                key={conversation.id}
                                index={index}
                                didUsers={didUsers}
                                conversation={conversation}
                            />
                        );
                    })}
            </InfiniteScroll>
        </List>
    );
};

export default ConversationList;
